import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useSimpleProfile } from '@/hooks/useSimpleProfile';
import { useToast } from '@/hooks/use-toast';
import { UserPreferences } from '@/types/profile';

// Accent color mapping to CSS custom properties
const accentColorMap = {
  blue: { primary: '217 91% 60%', secondary: '217 91% 70%' },
  purple: { primary: '262 83% 58%', secondary: '262 83% 68%' },
  green: { primary: '142 76% 36%', secondary: '142 76% 46%' },
  orange: { primary: '25 95% 53%', secondary: '25 95% 63%' },
  red: { primary: '0 84% 60%', secondary: '0 84% 70%' },
  pink: { primary: '330 81% 60%', secondary: '330 81% 70%' },
  indigo: { primary: '239 84% 67%', secondary: '239 84% 77%' },
  teal: { primary: '173 58% 39%', secondary: '173 58% 49%' },
  yellow: { primary: '48 96% 53%', secondary: '48 96% 63%' },
};

// Layout density classes
const layoutDensityMap = {
  compact: 'compact-layout',
  comfortable: 'comfortable-layout',
  spacious: 'spacious-layout',
};

// Border radius classes
const borderRadiusMap = {
  none: 'rounded-none',
  small: 'rounded-sm',
  medium: 'rounded-md',
  large: 'rounded-lg',
  full: 'rounded-full',
};

// Font size classes
const fontSizeMap = {
  small: 'text-sm',
  medium: 'text-base',
  large: 'text-lg',
  xlarge: 'text-xl',
};

export const useAppearanceSettings = () => {
  const { theme, setTheme } = useTheme();
  const { profile, updateProfile, isUpdating } = useSimpleProfile();
  const { toast } = useToast();
  
  const [preferences, setPreferences] = useState<UserPreferences>({
    theme: 'system',
    accent_color: 'blue',
    font_size: 'medium',
    layout_density: 'comfortable',
    animations_enabled: true,
    high_contrast: false,
    color_temperature: 'neutral',
    border_radius: 'medium',
    sidebar_style: 'modern',
    enhanced_contrast: false,
    glass_effect: false,
  });

  const [isApplying, setIsApplying] = useState(false);

  // Initialize preferences from profile
  useEffect(() => {
    if (profile?.preferences) {
      setPreferences({
        theme: theme,
        accent_color: profile.preferences.accent_color || 'blue',
        font_size: profile.preferences.font_size || 'medium',
        layout_density: profile.preferences.layout_density || 'comfortable',
        animations_enabled: profile.preferences.animations_enabled !== false,
        high_contrast: profile.preferences.high_contrast || false,
        color_temperature: profile.preferences.color_temperature || 'neutral',
        border_radius: profile.preferences.border_radius || 'medium',
        sidebar_style: profile.preferences.sidebar_style || 'modern',
        enhanced_contrast: profile.preferences.enhanced_contrast || false,
        glass_effect: profile.preferences.glass_effect || false,
      });
    }
  }, [profile, theme]);

  // Apply CSS custom properties for appearance settings
  const applyAppearanceSettings = useCallback((newPreferences: UserPreferences) => {
    setIsApplying(true);
    
    try {
      const root = document.documentElement;
      
      // Apply accent color
      if (newPreferences.accent_color && accentColorMap[newPreferences.accent_color as keyof typeof accentColorMap]) {
        const colors = accentColorMap[newPreferences.accent_color as keyof typeof accentColorMap];
        root.style.setProperty('--accent', colors.primary);
        root.style.setProperty('--accent-secondary', colors.secondary);
        root.style.setProperty('--ring', colors.primary);
      }

      // Apply border radius
      if (newPreferences.border_radius) {
        const radiusValue = {
          none: '0px',
          small: '4px',
          medium: '8px',
          large: '12px',
          full: '50px',
        }[newPreferences.border_radius] || '8px';
        
        root.style.setProperty('--radius', radiusValue);
      }

      // Apply layout density
      document.body.className = document.body.className
        .replace(/\b(compact|comfortable|spacious)-layout\b/g, '');
      
      if (newPreferences.layout_density && layoutDensityMap[newPreferences.layout_density as keyof typeof layoutDensityMap]) {
        document.body.classList.add(layoutDensityMap[newPreferences.layout_density as keyof typeof layoutDensityMap]);
      }

      // Apply font size
      document.body.className = document.body.className
        .replace(/\btext-(sm|base|lg|xl)\b/g, '');
      
      if (newPreferences.font_size && fontSizeMap[newPreferences.font_size as keyof typeof fontSizeMap]) {
        document.body.classList.add(fontSizeMap[newPreferences.font_size as keyof typeof fontSizeMap]);
      }

      // Apply high contrast
      if (newPreferences.high_contrast) {
        document.body.classList.add('high-contrast');
      } else {
        document.body.classList.remove('high-contrast');
      }

      // Apply glass effect
      if (newPreferences.glass_effect) {
        document.body.classList.add('glass-effect-enabled');
      } else {
        document.body.classList.remove('glass-effect-enabled');
      }

      // Apply animations preference
      if (newPreferences.animations_enabled === false) {
        document.body.classList.add('no-animations');
      } else {
        document.body.classList.remove('no-animations');
      }
      
    } catch (error) {
      console.error('Error applying appearance settings:', error);
    } finally {
      setIsApplying(false);
    }
  }, []);

  // Apply settings whenever preferences change
  useEffect(() => {
    applyAppearanceSettings(preferences);
  }, [preferences, applyAppearanceSettings]);

  const updatePreference = useCallback((key: keyof UserPreferences, value: any) => {
    setPreferences(prev => {
      const newPreferences = { ...prev, [key]: value };
      
      // Handle theme changes specially
      if (key === 'theme') {
        setTheme(value);
      }
      
      return newPreferences;
    });
  }, [setTheme]);

  const savePreferences = useCallback(async () => {
    if (!profile) return;

    try {
      await updateProfile({
        preferences: {
          ...profile.preferences,
          ...preferences,
        }
      });
      
      toast({
        title: "Appearance settings saved",
        description: "Your preferences have been applied successfully.",
      });
    } catch (error) {
      toast({
        title: "Error saving preferences",
        description: "Failed to save your appearance settings.",
        variant: "destructive",
      });
    }
  }, [preferences, profile, updateProfile, toast]);

  const resetToDefaults = useCallback(() => {
    const defaultPreferences: UserPreferences = {
      theme: 'system',
      accent_color: 'blue',
      font_size: 'medium',
      layout_density: 'comfortable',
      animations_enabled: true,
      high_contrast: false,
      color_temperature: 'neutral',
      border_radius: 'medium',
      sidebar_style: 'modern',
      enhanced_contrast: false,
      glass_effect: false,
    };
    
    setPreferences(defaultPreferences);
    setTheme('system');
    
    toast({
      title: "Settings reset",
      description: "All appearance settings have been reset to defaults.",
    });
  }, [setTheme, toast]);

  const applyPreset = useCallback((preset: Partial<UserPreferences>) => {
    setPreferences(prev => ({ ...prev, ...preset }));
    
    if (preset.theme && (preset.theme === 'light' || preset.theme === 'dark' || preset.theme === 'system')) {
      setTheme(preset.theme);
    }
    
    toast({
      title: "Preset applied",
      description: "Theme preset has been applied successfully.",
    });
  }, [setTheme, toast]);

  return {
    preferences,
    updatePreference,
    savePreferences,
    resetToDefaults,
    applyPreset,
    isUpdating: isUpdating || isApplying,
    hasUnsavedChanges: JSON.stringify(preferences) !== JSON.stringify(profile?.preferences || {}),
  };
};